/**
 * PageGuide — a compact "how this page is calculated" reference pinned at the
 * bottom of every page. One row per card: the card/section name, a plain-English
 * meaning, and the EXACT rule (source table / filter) so the numbers are
 * auditable. Definitions live beside each page and must be kept in sync with the
 * queries that actually produce the numbers.
 *
 * Props:
 *   title?:    string
 *   subtitle?: string
 *   groups:    [{ section: string, rows: [{ card, means, rule }] }]
 */
import React from "react";
import { tokensV2 as T } from "./tokens.v2";

export function PageGuide({ title = "How this page is calculated", subtitle, groups = [] }) {
  const real = (groups || []).filter(g => g && (g.rows || []).length);
  if (!real.length) return null;

  const th = {
    textAlign: "left", padding: "6px 12px", fontSize: 11, fontWeight: 700,
    textTransform: "uppercase", letterSpacing: 0.4, color: T.text.muted,
    borderBottom: `1px solid ${T.border.default}`, whiteSpace: "nowrap",
  };
  const td = {
    padding: "10px 12px", fontSize: 13, lineHeight: 1.5, color: T.text.secondary,
    borderBottom: `1px solid ${T.border.subtle}`, verticalAlign: "top",
  };

  return (
    <section style={{
      marginTop: 28, background: T.surface.card,
      border: `1px solid ${T.border.default}`, borderRadius: T.radius.lg,
      padding: T.space.xl,
    }}>
      <div style={{ fontSize: 15, fontWeight: 700, color: T.text.primary }}>{title}</div>
      <div style={{ fontSize: 12, color: T.text.muted, marginTop: 4, marginBottom: 18 }}>
        {subtitle || "Every card on this page — what it means and exactly how it's computed. Date-scoped cards follow the date picker at the top."}
      </div>

      {real.map((g, gi) => (
        <div key={gi} style={{ marginBottom: gi === real.length - 1 ? 0 : 22 }}>
          <div style={{
            fontSize: 12, fontWeight: 700, color: T.brand.base,
            textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6,
          }}>{g.section}</div>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 600 }}>
              <thead>
                <tr>
                  <th style={{ ...th, width: "20%" }}>Card</th>
                  <th style={{ ...th, width: "38%" }}>What it means</th>
                  <th style={{ ...th, width: "42%" }}>How it's calculated</th>
                </tr>
              </thead>
              <tbody>
                {g.rows.map((r, ri) => (
                  <tr key={ri}>
                    <td style={{ ...td, fontWeight: 600, color: T.text.primary }}>{r.card}</td>
                    <td style={td}>{r.means}</td>
                    <td style={{ ...td, color: T.text.muted, fontSize: 12.5 }}>{r.rule}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </section>
  );
}
